import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { cn } from '../lib/utils';
import { ErrorBoundary } from './ErrorBoundary';
import { DashboardLoadingState } from './LoadingStates';
import { MobileHeader } from './MobileHeader';
import { MobileSidebar } from './MobileSidebar';
import { PublisherFrozenBanner } from './PublisherFrozenBanner';
import { PublisherVerificationBanner } from './PublisherVerificationBanner';
import { EASE_OUT } from './portal';
import { Sidebar } from './sidebar/Sidebar';
import { SidebarDataProvider } from './sidebar/hooks/useSidebarData';

const DRAWER_ROUTES = ['/new-dapp', '/new-version'] as const;

const DRAWER_ANIMATION = {
  /* The page recedes slightly behind the sheet; 0.98 keeps text legible. */
  scale: { open: 0.98, closed: 1 },
  opacity: { open: 0.4, closed: 1 },
  transition: { duration: 0.2, ease: EASE_OUT },
  /* Critically damped (ζ ≈ 1): settles in ~300ms with no overshoot. */
  spring: {
    type: 'spring' as const,
    damping: 36,
    stiffness: 400,
    mass: 0.8,
  },
} as const;

export const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDrawerOpen = DRAWER_ROUTES.some((route) =>
    location.pathname.endsWith(route),
  );
  const previousLocation = useRef<string>('/dashboard');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleClose = () => {
    navigate(previousLocation.current);
  };

  // Any navigation (incl. rows without onLinkClick) closes the mobile drawer.
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDrawerOpen) {
      previousLocation.current = `${location.pathname}${location.search}${location.hash}`;
    }
  }, [location.hash, location.pathname, location.search, isDrawerOpen]);

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          navigate(previousLocation.current);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isDrawerOpen, navigate]);

  return (
    <ErrorBoundary>
      <SidebarDataProvider>
        <div className="relative flex h-screen flex-col overflow-hidden bg-seeker-950">
          <PublisherVerificationBanner />

          <div className="flex min-h-0 flex-1">
            <div className="hidden h-full lg:block">
              <Sidebar />
            </div>

            <MobileSidebar
              isOpen={isMobileSidebarOpen}
              onClose={() => setIsMobileSidebarOpen(false)}
            >
              <Sidebar
                className="w-full h-full bg-transparent"
                onLinkClick={() => setIsMobileSidebarOpen(false)}
              />
            </MobileSidebar>

            <div className="flex-1 flex flex-col h-full min-w-0">
              <MobileHeader onMenuClick={() => setIsMobileSidebarOpen(true)} />
              <PublisherFrozenBanner />

              <motion.div
                animate={{
                  scale: isDrawerOpen
                    ? DRAWER_ANIMATION.scale.open
                    : DRAWER_ANIMATION.scale.closed,
                  opacity: isDrawerOpen
                    ? DRAWER_ANIMATION.opacity.open
                    : DRAWER_ANIMATION.opacity.closed,
                }}
                transition={DRAWER_ANIMATION.transition}
                className={cn(
                  'flex flex-1 flex-col overflow-auto bg-seeker-900 md:m-2 md:rounded-xl md:border md:border-seeker-800 lg:ml-0',
                  isDrawerOpen && 'pointer-events-none',
                )}
              >
                <ErrorBoundary>
                  <Suspense fallback={<DashboardLoadingState />}>
                    {/* The drawer covers the frame; mounting the previous route here made
                        DashboardMain redirect away from deep links to /new-dapp & /new-version. */}
                    {isDrawerOpen ? null : <Outlet />}
                  </Suspense>
                </ErrorBoundary>
              </motion.div>
            </div>
          </div>

          <AnimatePresence>
            {isDrawerOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: EASE_OUT }}
                  className="fixed inset-0 z-40 bg-black/50"
                  onClick={handleClose}
                />

                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={DRAWER_ANIMATION.spring}
                  className="fixed inset-x-0 bottom-0 z-50 h-full"
                >
                  <div className="h-full overflow-hidden bg-seeker-950 shadow-2xl">
                    <Outlet />
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </SidebarDataProvider>
    </ErrorBoundary>
  );
};
